import { useEffect, useMemo, useRef, useState } from "react";
import { useGuestBuiltLoyaltyOffers } from "../../../../services/hooks/loyalty/useGuestBuiltLoyaltyOffers";
import { useCategoryBarLogic } from "../../../../services/hooks/useCategoryBarLogic";
import { DISPLAYED_NAME } from "../../../../utils/enums";
import { getDisplayedName } from "../../../../utils/help/localization/localization";
import { LoyaltyOfferIcon } from "../../../global/LoyaltyOfferIcon/LoyaltyOfferIcon";
import { PTab } from "../../../shared/PTab/PTab";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { SPECIALLY_FOR_YOU_ANCHOR } from "../SpeciallyForYou/SpeciallyForYouSection";
import { isVisitor } from "../../../../utils/help/user/user";
import { CategoryType } from "../../../../utils/types";
import styles from "./CategoryBarV2.module.scss";

export const BAR_ID = "category_bar_new";
const SCROLL_HEADER_OFFSET = 150;
const CLICK_SCROLL_LOCK_MS = 700;

type TabId = string | number;

export const CategoryBarV2 = (props: {
  categories: CategoryType[];
  areaAboveNavBar: number | undefined;
  searchWrapperBarHeight: number;
  leading?: React.ReactNode;
  leadingRef?: React.RefObject<HTMLDivElement>;
  leadingExpanded?: boolean;
}) => {
  const { isFranchiseOpen, isMainDark, scrollToCategory, trackCategoryClick } =
    useCategoryBarLogic();
  const { t } = useTranslationI18();
  const guestBuiltOffers = useGuestBuiltLoyaltyOffers();
  const hasSpeciallyForYou = guestBuiltOffers.length > 0;

  // Every section the bar has a tab for, in page order. "Specially for you"
  // sits above the menu, so it is the first tab whenever it is shown.
  const tabIds = useMemo<TabId[]>(
    () => [
      ...(hasSpeciallyForYou ? [SPECIALLY_FOR_YOU_ANCHOR] : []),
      ...(props.categories ?? []).map((cat) => cat.id),
    ],
    [hasSpeciallyForYou, props.categories]
  );

  const initialId = tabIds[0] ?? null;
  const [activeCategoryId, setActiveCategoryId] = useState<TabId | null>(
    initialId
  );
  // Ref mirrors state so the scroll handler reads current value without stale closure
  const activeCategoryRef = useRef<TabId | null>(initialId);
  // Prevents the scroll handler from overriding active state during a click-triggered
  // smooth scroll, where intermediate page positions cause wrong categories to match
  const isClickScrollingRef = useRef(false);
  const clickScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Effect: scroll the bar horizontally whenever the active category changes
  // Kept separate from the scroll handler to avoid feedback loops
  useEffect(() => {
    const categoryBarElm = document.querySelector<HTMLElement>(`#${BAR_ID} ul`);
    const navElm = document.querySelector<HTMLElement>(
      `#navElm_new_${activeCategoryId}`
    );

    if (!categoryBarElm || !navElm) {
      return;
    }

    // The list's content edge, not its border edge: the tab-bar layout keeps the
    // page gutter as the list's own padding, so aligning to the border edge
    // parked the active tab flush against the screen.
    const listStart =
      categoryBarElm.getBoundingClientRect().left +
      parseFloat(getComputedStyle(categoryBarElm).paddingLeft);

    categoryBarElm.scrollTo({
      top: 0,
      left:
        categoryBarElm.scrollLeft +
        navElm.getBoundingClientRect().left -
        listStart,
      behavior: "smooth",
    });
  }, [activeCategoryId]);

  // Effect: track the active category based on page scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (isClickScrollingRef.current) {
        return;
      }

      const categoryBarElm = document.querySelector<HTMLElement>(
        `#${BAR_ID} ul`
      );
      const offset =
        (categoryBarElm?.getBoundingClientRect().top ?? 0) +
        SCROLL_HEADER_OFFSET;

      // The last section scrolled up to the bar; before any has, the first,
      // since that is the one on screen.
      const nextId = tabIds.reduce<TabId | null>((current, id) => {
        const section = document.querySelector<HTMLElement>(`#category_${id}`);

        return section &&
          Math.trunc(section.getBoundingClientRect().top) <= offset
          ? id
          : current;
      }, tabIds[0] ?? null);

      if (activeCategoryRef.current !== nextId) {
        activeCategoryRef.current = nextId;
        setActiveCategoryId(nextId);
      }
    };

    // Sections can mount after the bar (the offers load on their own), so
    // place the active tab now rather than waiting for the first scroll.
    handleScroll();
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      if (clickScrollTimeoutRef.current) {
        clearTimeout(clickScrollTimeoutRef.current);
      }
    };
  }, [tabIds]);

  const selectTab = (id: TabId) => {
    activeCategoryRef.current = id;
    setActiveCategoryId(id);

    // Hold the scroll handler off for the smooth scroll, whose intermediate
    // positions would otherwise mark the sections it passes on the way.
    isClickScrollingRef.current = true;
    if (clickScrollTimeoutRef.current) {
      clearTimeout(clickScrollTimeoutRef.current);
    }
    clickScrollTimeoutRef.current = setTimeout(() => {
      isClickScrollingRef.current = false;
    }, CLICK_SCROLL_LOCK_MS);
  };

  const topOffset =
    (props.areaAboveNavBar ?? 0) + (props.searchWrapperBarHeight ?? 0);

  return (
    <div
      id={BAR_ID}
      className={`${styles.category_bar} ${
        !isFranchiseOpen ? styles.closed : ""
      } ${isMainDark ? "bg__zero" : "bg__02"} ${
        isVisitor() ? styles.extra_top_space : ""
      } ${props.leading ? styles.has_leading : ""} ${
        props.leadingExpanded ? styles.leading_expanded : ""
      }`}
      style={{ top: `${topOffset}px` }}
    >
      {props.leading && <div className={styles.leading}>{props.leading}</div>}
      <ul className={styles.category_bar__list}>
        {hasSpeciallyForYou && (
          <li>
            <PTab
              href={`#category_${SPECIALLY_FOR_YOU_ANCHOR}`}
              id={`navElm_new_${SPECIALLY_FOR_YOU_ANCHOR}`}
              isActive={activeCategoryId === SPECIALLY_FOR_YOU_ANCHOR}
              tone="loyalty"
              icon={
                <LoyaltyOfferIcon
                  offer={guestBuiltOffers[0]}
                  giftIcon
                  iconOnly
                  backgroundIconColor="loyalty"
                  width={20}
                />
              }
              onClick={(e) => {
                selectTab(SPECIALLY_FOR_YOU_ANCHOR);
                scrollToCategory(e, props.searchWrapperBarHeight);
              }}
            >
              {t("offers:specially-for-you")}
            </PTab>
          </li>
        )}
        {props.categories?.map((cat) => (
          <li key={cat.id}>
            <PTab
              href={`#category_${cat.id}`}
              id={`navElm_new_${cat.id}`}
              isActive={activeCategoryId === cat.id}
              onClick={(e) => {
                selectTab(cat.id);
                scrollToCategory(e, props.searchWrapperBarHeight);
                trackCategoryClick(Number(cat.id));
              }}
            >
              {getDisplayedName(cat, DISPLAYED_NAME.CATEGORY_NAME)}
            </PTab>
          </li>
        ))}
      </ul>
    </div>
  );
};
